"use client";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import SwiperButton from "../../elements/buttons/SwiperButton";
import { encodeImageUrl } from "@/app/utils/encodeImageUrl";
import { SliderItem } from "@/app/lib/features/home/home.types";

interface BannerSectionProps {
  /** /home/sections/slider → data.items */
  items?: SliderItem[];
}

/**
 * API'den gelen banner'ı bileşenin ihtiyaç duyduğu minimal şekle indirger.
 *
 * `image_url` ve `image` aynı değeri taşıyor (ikisi de dolu geliyor); yine de
 * ikisine de bakılıyor çünkü hangisinin kalıcı olduğu garanti değil.
 *
 * mobile_image API'de YOK — bilerek opsiyonel bırakıldı: alan eklendiğinde
 * burada tek satır dolar, aşağıdaki <source media> ve oran mantığı hazır.
 */
const normalize = (item: SliderItem) => ({
  id: item.id,
  image: item.image_url || item.image || "",
  mobileImage: undefined as string | undefined,
  title: item.title || "",
  href: item.link?.url || "",
});

// Oranların TEK kaynağı burası — globals.css .banner-bleed bu iki değişkeni
// okuyor, orada sabit oran yok. Değiştirmek için tek yer bu iki satır.
const DESKTOP_ASPECT = "1712 / 360"; // md ve üstü — 4.76:1 geniş şerit
/** Telefon (<768px) oranı — yalnızca HER banner'ın mobile_image'ı varsa. */
const MOBILE_ASPECT = "3 / 2";
const AUTOPLAY_DELAY = 3000;

const BannerSection = ({ items }: BannerSectionProps) => {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  // Görseli olmayan kayıt banner olamaz — boş kutu basmak yerine elenir.
  const banners = useMemo(
    () => (items ?? []).map(normalize).filter((b) => b.image),
    [items],
  );

  // AUTOPLAY'İ EKRAN DIŞINDA DURDUR.
  //
  // Swiper autoplay banner görünmese de çalışmaya devam ediyordu: kullanıcı
  // sayfanın ortasındayken bile 3 saniyede bir slide geçişi tetikleniyor,
  // `loop` yüzünden Swiper DOM'da slide taşıyor (loopFix) ve her geçiş
  // onSlideChange → setState ile React render'ı doğuruyordu. Aşağı kaydırırken
  // periyodik takılmanın kaynağı buydu — ekranda olmayan bir bileşen için
  // yapılan tamamen boşa iş.
  //
  // IntersectionObserver kaydırma sırasında kare başına çalışmaz; yalnızca
  // banner görünürlüğü değiştiğinde tetiklenir.
  useEffect(() => {
    const el = rootRef.current;
    const autoplay = swiper?.autoplay;
    if (!el || !autoplay) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) autoplay.start();
        else autoplay.stop();
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [swiper]);

  if (banners.length === 0) return null;

  // Tek banner: ok, pagination ve autoplay yok; dokunmatik kaydırma da kapalı.
  const hasMultiple = banners.length > 1;

  // Mobil oran ancak TÜM banner'ların mobil kreatifi varsa devreye girer.
  // Biri bile eksikse 4.76:1'lik masaüstü görselini 3:2 kutuya sokmak
  // genişliğin ~%68'ini kırpardı; onun yerine oran masaüstünde bırakılıyor.
  //
  // ŞU AN: API mobil görsel göndermiyor, yani bu daima false ve telefonda
  // banner 4.76:1 oranında (~390px ekranda ~82px) bir şerit oluyor. Çözüm
  // kodda değil veride: /home/sections/slider items[]'ına mobil bir alan
  // eklenmeli. Alan geldiğinde normalize()'da mobileImage'ı doldurmak yeterli.
  const hasMobileArt = banners.every((b) => !!b.mobileImage);

  return (
    <section style={{ marginTop: 10 }}>
      {/* DİKKAT: buraya `w-full` (width:100%) EKLEMEYİN.
          Mobildeki tam genişlik `.banner-bleed`in negatif margin'iyle yapılıyor;
          negatif margin ancak `width: auto` iken kutuyu GENİŞLETİR. Genişlik
          açıkça verilirse kutu 100%'de kilitlenir, margin onu yalnızca sola
          kaydırır ve sağda viewport'un ~%10'u kadar boşluk kalır. */}
      <div
        ref={rootRef}
        className="banner-bleed relative overflow-hidden bg-gray-100 md:rounded-2xl"
        style={
          {
            "--banner-ar-desktop": DESKTOP_ASPECT,
            "--banner-ar-mobile": hasMobileArt ? MOBILE_ASPECT : DESKTOP_ASPECT,
          } as CSSProperties
        }
      >
        <Swiper
          className="absolute inset-0 w-full h-full"
          modules={hasMultiple ? [Autoplay] : []}
          slidesPerView={1}
          spaceBetween={0}
          loop={hasMultiple}
          allowTouchMove={hasMultiple}
          autoplay={
            hasMultiple
              ? {
                  delay: AUTOPLAY_DELAY,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          onSwiper={setSwiper}
          // loop açıkken activeIndex klonlanmış slide'ları da sayar —
          // pagination gerçek sırayı gösterecekse realIndex şart.
          onSlideChange={(s) => setActiveIndex(s.realIndex)}
        >
          {banners.map((banner, i) => {
            /* next/image yerine <picture>: <source media> ile mobil/masaüstü
               kreatifleri arasında gerçek art direction yapılabiliyor ve
               tarayıcı yalnızca BİRİNİ indiriyor. İki next/image'i md:hidden ile
               gizlemek her ikisini de indirtirdi — next/image'in tek bir src'ye
               bağlı olması bu senaryoyu desteklemiyor.

               NOT: images.unoptimized kaldırıldıktan sonra bu blok resize/format
               dönüşümünden yararlanmayan tek yer olarak kaldı; banner kreatifi
               1712×360 olarak inip mobilde 721×152'de gösteriliyor (~385 KB).
               Kalıcı çözüm banner'ları API tarafında doğru boyutta servis etmek;
               alternatif olarak <source srcSet> değerleri /_next/image URL'leriyle
               elle kurulabilir (w değerleri deviceSizes, q değeri qualities
               listesinde OLMAK ZORUNDA — yoksa optimizer 400 döner). */
            const image = (
              <picture>
                {banner.mobileImage && (
                  <source
                    media="(max-width: 767px)"
                    srcSet={encodeImageUrl(banner.mobileImage)}
                  />
                )}
                <img
                  src={encodeImageUrl(banner.image)}
                  alt={banner.title || "Banner"}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : "auto"}
                  decoding="async"
                  draggable={false}
                />
              </picture>
            );

            return (
              <SwiperSlide key={banner.id} className="relative w-full h-full">
                {banner.href ? (
                  <Link
                    href={banner.href}
                    className="block w-full h-full"
                    aria-label={banner.title || "Banner"}
                  >
                    {image}
                  </Link>
                ) : (
                  image
                )}
              </SwiperSlide>
            );
          })}
          {hasMultiple && (
            <div className="absolute z-50 top-1/2 -translate-y-1/2 left-0 w-full items-center justify-between px-3 pointer-events-none hidden md:flex">
              <SwiperButton position="left" />
              <SwiperButton position="right" />
            </div>
          )}
        </Swiper>
        {/* Telefonda bu noktalara kimse basmıyor (slider zaten kaydırılıyor),
            o yüzden mobilde yalnızca konum göstergesi boyutunda; tıklama
            hedefi md ve üstünde büyüyor. */}
        {hasMultiple && (
          <div className="absolute bottom-2 md:bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 md:gap-2 rounded-full bg-black/25 px-2 py-1 md:px-3 md:py-2 backdrop-blur-sm">
            {banners.map((banner, i) => (
              <button
                key={banner.id}
                type="button"
                aria-label={`${i + 1}. banner`}
                aria-current={i === activeIndex}
                onClick={() => swiper?.slideToLoop(i)}
                className={`h-1 md:h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  i === activeIndex
                    ? "w-4 md:w-6 bg-white"
                    : "w-1 md:w-2 bg-white/60 hover:bg-white/90"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BannerSection;
